import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY, scrollToId } from "../data/site";
import { useLang } from "../context/LangContext";

const LangSwitcher = ({ className = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div data-testid="lang-switcher" className={`flex items-center rounded-full border border-white/15 p-0.5 ${className}`}>
      {["fr", "de"].map((l) => (
        <button
          key={l}
          data-testid={`lang-switch-${l}`}
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          className={`px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest transition-colors duration-300 ${
            lang === l ? "bg-sky-600 text-white" : "text-slate-300 hover:text-white"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { S } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, hash) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(hash);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 border-b ${
        scrolled
          ? "bg-[#0A182E]/90 backdrop-blur-xl border-white/10 shadow-lg shadow-black/20"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-[72px] items-center justify-between gap-4">
          <a
            href="#accueil"
            onClick={(e) => go(e, "#accueil")}
            data-testid="header-logo-link"
            className="flex items-center gap-3 shrink-0"
            aria-label="GPN Sàrl"
          >
            <span className="flex items-center gap-3">
              <img src="/logo-emblem-dark.png" alt="GPN Sàrl — emblème abeille" className="h-10 w-auto" />
              <span className="hidden sm:flex flex-col leading-tight">
                <span className="font-display font-bold text-white text-lg tracking-tight">GPN Sàrl</span>
                <span className="font-tech text-[9px] font-semibold uppercase tracking-[0.22em] text-sky-400">
                  {COMPANY.tagline}
                </span>
              </span>
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Navigation principale">
            {S.nav.map((item) => (
              <a
                key={item.hash}
                href={item.hash}
                onClick={(e) => go(e, item.hash)}
                data-testid={`nav-link-${item.hash.slice(1)}`}
                className="text-sm font-medium text-slate-200 hover:text-sky-400 transition-colors duration-300 tracking-wide"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <LangSwitcher className="hidden md:flex" />
            <a
              href="#devis"
              onClick={(e) => go(e, "#devis")}
              data-testid="header-cta-devis"
              className="hidden sm:inline-flex items-center rounded-full bg-sky-600 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-sky-950/40 transition-all duration-300 hover:bg-sky-500 hover:shadow-sky-600/30 hover:-translate-y-0.5"
            >
              {S.ctaDevis}
            </a>
            <button
              data-testid="header-mobile-menu-button"
              onClick={() => setOpen(!open)}
              className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-white/15 text-white"
              aria-label="Menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-[#0A182E] border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {S.nav.map((item, i) => (
                <motion.a
                  key={item.hash}
                  href={item.hash}
                  onClick={(e) => go(e, item.hash)}
                  data-testid={`mobile-nav-link-${item.hash.slice(1)}`}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  className="py-3 text-lg font-display font-semibold text-white border-b border-white/5"
                >
                  {item.label}
                </motion.a>
              ))}
              <a
                href="#devis"
                onClick={(e) => go(e, "#devis")}
                data-testid="mobile-cta-devis"
                className="mt-5 inline-flex items-center justify-center rounded-full bg-sky-600 px-6 py-3.5 text-sm font-semibold text-white"
              >
                {S.ctaDevis}
              </a>
              <div className="mt-3 grid grid-cols-2 gap-3">
                {COMPANY.phones.map((p, i) => (
                  <a
                    key={p.href}
                    href={p.href}
                    data-testid={`mobile-call-button-${i + 1}`}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-4 py-3 text-sm font-medium text-white"
                  >
                    <Phone className="w-4 h-4 text-sky-400" /> {p.label}
                  </a>
                ))}
              </div>
              <LangSwitcher className="mt-4 self-start" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
